import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Repeat, FileEdit, CalendarClock } from "lucide-react";

/**
 * Presented when the user tries to edit a transaction that was generated
 * from a recurring rule. Lets the user pick:
 *   - Edit this transaction only  → onEditOne()
 *   - Edit future occurrences     → onEditFuture()
 *
 * The dialog itself does not mutate anything — it just routes the intent
 * so historical transactions and the rule stay independent.
 */
export default function EditScopeDialog({ open, setOpen, onEditOne, onEditFuture }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="bg-[#151A23] border-white/10 text-white max-w-md"
        data-testid="edit-scope-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Repeat className="w-4 h-4 text-white/60" />
            Recurring transaction
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-white/70 mb-3">
          This transaction came from a recurring rule. What would you like to change?
        </div>
        <div className="space-y-2">
          <button
            data-testid="edit-scope-only-btn"
            onClick={() => { setOpen(false); onEditOne?.(); }}
            className="w-full text-left p-4 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition"
          >
            <div className="flex items-center gap-2 text-white font-medium">
              <FileEdit className="w-4 h-4 text-white/70" />
              Edit this transaction only
            </div>
            <div className="text-xs text-white/50 mt-1">
              Change just this one ledger entry. The recurring rule is not modified.
            </div>
          </button>
          <button
            data-testid="edit-scope-future-btn"
            onClick={() => { setOpen(false); onEditFuture?.(); }}
            className="w-full text-left p-4 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition"
          >
            <div className="flex items-center gap-2 text-white font-medium">
              <CalendarClock className="w-4 h-4 text-white/70" />
              Edit future occurrences
            </div>
            <div className="text-xs text-white/50 mt-1">
              Update the recurring rule. Past transactions already generated are unchanged.
            </div>
          </button>
        </div>
        <DialogFooter className="mt-4">
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            className="text-white/70 hover:text-white hover:bg-white/10"
            data-testid="edit-scope-cancel-btn"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
